import { useCallback } from "react";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import {
  GenerateUrlRequestBody,
  Paginated,
  PaginationQuery,
  UpdateOrderRequestBody,
  UpdateVisibilityRequestBody,
} from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  AdminNormative,
  InsertNormativeBody,
  UpdateNormativeNewImageBody,
  UpdateNormativeRemoveImageBody,
} from "./types/normatives";

export const useNormativesApi = () => {
  const api = useApi();

  const getAdminNormative = useCallback(
    (signal: AbortSignal) => {
      const url = `/admin/settings/patrol/normatives`;
      return useSWRMutation<AdminNormative, CustomError, string, string>(url, (url, { arg }: { arg: string }) =>
        api.get<AdminNormative>({ url: `${url}/${arg}`, signal }),
      );
    },
    [api],
  );

  const useAdminNormatives = useCallback(
    (query: PaginationQuery, signal: AbortSignal, systemId?: string) => {
      const url = buildUrl(
        systemId ? `/admin/settings/patrol/systems/${systemId}/normatives` : "/admin/settings/patrol/normatives",
        query,
      );
      return useSWR<Paginated<AdminNormative>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminNormative>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminNormative = useCallback(
    async (body: InsertNormativeBody): Promise<{ id: string }> => {
      const url = `/admin/settings/patrol/normatives`;
      const id = await api.post<{ id: string }, InsertNormativeBody>(url, body);
      mutateCache([url]);
      return id;
    },
    [api],
  );

  const updateAdminNormative = useCallback(
    async (normativeId: string, body: UpdateNormativeRemoveImageBody | UpdateNormativeNewImageBody): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}`;
      await api.put<UpdateNormativeRemoveImageBody | UpdateNormativeNewImageBody>(url, body);
      mutateCache(["/admin/settings/patrol/normatives"]);
    },
    [api],
  );

  const updateAdminNormativeOrder = useCallback(
    async (normativeId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache(["/admin/settings/patrol/normatives"]);
    },
    [api],
  );

  const deleteAdminNormative = useCallback(
    async (normativeId: string): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}`;
      await api.del(url);
      mutateCache([`/admin/settings/patrol/normatives`]);
    },
    [api],
  );

  const generateUploadImageUrl = useCallback(
    async (normativeId: string, fileName: string, fileType: string): Promise<string> => {
      const encodedFileName = encodeURIComponent(fileName);
      const url = `/admin/settings/patrol/normatives/${normativeId}/image/generate_upload_url?file_name=${encodedFileName}`;
      return api.post<string, GenerateUrlRequestBody>(url, {
        filename: fileName,
        contentType: fileType,
      });
    },
    [api],
  );

  const updateAdminNormativeVisibility = useCallback(
    async (normativeId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/patrol/normatives/${normativeId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache(["/admin/settings/patrol/normatives"]);
    },
    [api],
  );

  return {
    getAdminNormative,
    useAdminNormatives,
    insertAdminNormative,
    updateAdminNormative,
    updateAdminNormativeVisibility,
    updateAdminNormativeOrder,
    deleteAdminNormative,
    generateUploadImageUrl,
  };
};
